/* CASH REPORT — the charts above the three reports (Max, 2026-10-09: "tôi muốn thêm phần chart 3
   cái report này và design và layout hợp lý giúp tôi nhé").

   Each chart answers the first question its report is opened for, and draws only figures the table
   under it already prints, so the two cannot disagree:
     Weekly Cash Report   how much cash each entity holds and how much of it can be used
                          (Available / Restricted / Fixed deposit), and where the GROUP is heading
                          over the last twelve weeks
     Monthly Cash Plan    the group's ending cash month by month, and Operation against Finance &
                          Investment (the 13 Weeks Cash Plan's chart was replaced on 2026-10-25)
     Month-End Reconcile  the month-end cash per entity, and how many accounts reconcile

   COLOUR IS NEVER THE ONLY CODE. Inflow and outflow are also UP and DOWN; plan and actual are also a
   dashed and a solid line; the reconcile bars are blue against red (not green against red), and
   every series is named in its legend and its tooltip. A week nobody reported is a GAP in a line,
   never a zero — a zero would read as the cash having gone. */
import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line,
  ReferenceLine,
} from 'recharts';
import { API, call, fmtDay, moneyIn, toBase } from './cashUi';

const NAVY = '#1F3864';
const C = {
  /* THE REPORT TABLES' OWN CODE (Max, 2026-10-15: "màu … cần chỉnh lại cho hợp lí hơn"): Tuan's Field 1
     and Field 2 head Available in blue and Restricted / Fixed deposit in red — cash that can be used,
     and cash that cannot. The chart says the same: Available that blue, Restricted that red, Fixed
     deposit a lighter red beside it (the grey it had read as "nothing there"). Blue and the dark red
     are the same lightness, so the segments are parted by a white line, not by colour alone. */
  available: '#0369a1', restricted: '#b91c1c', fixed: '#f87171',
  closing: NAVY, availableLine: '#0ea5e9',
  inflow: '#16a34a', outflow: '#dc2626', planClose: NAVY, actualClose: '#d97706',
  reconciled: '#0369a1', difference: '#b91c1c', monthEnd: NAVY,
};
// 1,860,000 → 1.86M; 661,450 → 661k — the axis; the tooltip carries the exact figure.
const compact = (v) => {
  if (v == null || Number.isNaN(v)) return '';
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(2).replace(/\.?0+$/, '')}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
  return String(Math.round(v));
};
// Whole numbers on every chart (Finance team, 2026-10-21: "No need to show decimal").
const tip = (v) => (v == null ? '—' : moneyIn(v, 'SGD', 0));
const axis = { fontSize: 11, fill: '#475569' };

function Card({ title, sub, children, wide }) {
  return (
    <div className={`cw-chart${wide ? ' wide' : ''}`}>
      <h4>{title}</h4>
      {sub && <p className="cw-chart-sub">{sub}</p>}
      {children}
    </div>
  );
}
const Empty = ({ text }) => <div className="cw-chart-empty">{text}</div>;

/* ---- Weekly Cash Report --------------------------------------------------------------------- */
export function WeeklyCharts({ t, data, base = 'SGD' }) {
  const [trend, setTrend] = useState(null);
  const code = data.period.code;
  useEffect(() => {
    let live = true;
    setTrend(null);
    call(`${API}/trend/weekly?period=${encodeURIComponent(code)}`).then((j) => { if (live) setTrend(j.points); }).catch(() => { if (live) setTrend([]); });
    return () => { live = false; };
  }, [code]);

  /* In the base currency the reader picked (Finance team, 2026-10-21): this week's bars at this week's
     rate, each trend point at ITS OWN week's rate — a 12-week line at one rate would show the currency
     moving as cash moving. Whole numbers, as the table. */
  const cv = (v, rates) => toBase(v, rates || data.rates, base);
  const tipB = (v) => (v == null ? '—' : moneyIn(v, base, 0));
  const rows = data.entities.filter((e) => e.totals).map((e) => ({
    name: e.entity,
    available: Math.max(0, cv(e.totals.available)), restricted: cv(e.totals.restricted), fixed: cv(e.totals.fixed_deposit), closing: cv(e.totals.closing),
  })).sort((a, b) => b.closing - a.closing);
  const reported = rows.length; const of = data.entities.length;
  const points = (trend || []).map((p) => ({ ...p, label: p.week, closing: cv(p.closing, p.rates), available: cv(p.available, p.rates) }));
  const anyTrend = points.some((p) => p.closing != null);
  return (
    <div className="cw-charts" id="cw-charts">
      <Card title={t('cashc.byEntity', { ccy: base })} sub={t('cashc.byEntitySub', { n: reported, of })}>
        {rows.length ? (
          <ResponsiveContainer width="100%" height={Math.max(150, 46 + rows.length * 34)}>
            <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={compact} tick={axis} />
              <YAxis type="category" dataKey="name" width={56} tick={{ ...axis, fontWeight: 700 }} />
              <Tooltip formatter={(v, n) => [tipB(v), n]} />
              {/* In the order the bar is stacked, not alphabetical (recharts' default). */}
              <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
              <Bar dataKey="available" stackId="c" name={t('cashw.tot_available')} fill={C.available} stroke="#fff" strokeWidth={1} />
              <Bar dataKey="restricted" stackId="c" name={t('cashw.tot_restricted')} fill={C.restricted} stroke="#fff" strokeWidth={1} />
              <Bar dataKey="fixed" stackId="c" name={t('cashw.tot_fixed_deposit')} fill={C.fixed} stroke="#fff" strokeWidth={1} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty text={t('cashc.noneYet')} />}
      </Card>
      <Card title={t('cashc.trend', { ccy: base })} sub={t('cashc.trendSub')}>
        {trend === null ? <Empty text={t('cash.loading')} /> : anyTrend ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={axis} interval="preserveStartEnd" />
              <YAxis tickFormatter={compact} tick={axis} width={56} />
              <Tooltip formatter={(v, n) => [tipB(v), n]}
                labelFormatter={(l, p) => (p?.[0] ? `${l} · ${fmtDay(p[0].payload.end)} · ${t('cashc.reported', { n: p[0].payload.reported, of: p[0].payload.of })}` : l)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="closing" name={t('cashw.tot_balance')} stroke={C.closing} strokeWidth={2.5} dot={{ r: 3 }} connectNulls={false} />
              <Line type="monotone" dataKey="available" name={t('cashw.tot_available')} stroke={C.availableLine} strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3 }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty text={t('cashc.noTrend')} />}
      </Card>
    </div>
  );
}

/* ---- Monthly Cash Plan (2026-10-25) ----------------------------------------------------------
   The group's ending cash month by month, and where each month's change comes from — Operation against
   Finance & Investment, the Group CF's own split. One axis each (two charts, never a dual axis); the two
   flows are also UP and DOWN, and named in the legend and tooltip. In thousands of the base the reader
   picked, the table's own figures. A month nobody planned is a gap, never a zero. */
export function MonthPlanCharts({ t, data, base = 'SGD', conv }) {
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const lab = (ym) => `${MON[Number(ym.slice(5)) - 1]}-${ym.slice(2, 4)}`;
  const k = (v) => { const x = conv(v); return x == null ? null : Math.round(x / 1000); };
  const pts = (data.group || []).map((g) => ({ label: lab(g.month), end: k(g.end), op: k(g.op), fi: k(g.fi), net: k(g.net) }));
  const tipK = (v) => (v == null ? '—' : `${Number(v).toLocaleString('en-US')} K ${base}`);
  if (!pts.length) return <div className="cw-charts one"><Card wide title={t('xCashMp.chartEnd', { who: 'GROUP' })}><Empty text={t('xCashMp.chartNone')} /></Card></div>;
  return (
    <div className="cw-charts" id="mp-charts">
      <Card title={t('xCashMp.chartEnd', { who: 'GROUP' })} sub={t('xCashMp.chartEndSub', { base })}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={pts} margin={{ top: 8, right: 16, bottom: 0, left: 8 }} barSize={22}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={axis} />
            <YAxis tickFormatter={compact} tick={axis} width={56} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <Tooltip formatter={(v, n) => [tipK(v), n]} />
            <Bar dataKey="end" name={t('xCashMp.r_end')} fill={C.closing} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card title={t('xCashMp.chartNet', { who: 'GROUP' })} sub={t('xCashMp.chartNetSub', { base })}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={pts} margin={{ top: 8, right: 16, bottom: 0, left: 8 }} stackOffset="sign" barSize={22}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={axis} />
            <YAxis tickFormatter={compact} tick={axis} width={56} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <Tooltip formatter={(v, n) => [tipK(v), n]}
              labelFormatter={(l, p) => (p?.[0] ? `${l} · ${t('xCashMp.r_net')}: ${tipK(p[0].payload.net)}` : l)} />
            <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
            <Bar dataKey="op" stackId="f" name={t('xCashMp.r_op').replace(/^–\s*/, '')} fill={C.available} stroke="#fff" strokeWidth={1} />
            <Bar dataKey="fi" stackId="f" name={t('xCashMp.r_fi').replace(/^–\s*/, '')} fill={C.actualClose} stroke="#fff" strokeWidth={1} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

/* ---- Month-End Reconcile -------------------------------------------------------------------- */
export function MonthEndCharts({ t, data }) {
  const rows = data.entities.filter((e) => e.totals).map((e) => ({
    name: e.entity, reconciled: e.totals.reconciled,
    difference: Math.max(0, e.totals.accounts - e.totals.reconciled), unconverted: e.totals.unconverted || 0,
    /* NO RATE IS NOT ZERO: with every account unconverted the server's sum is 0, and a bar at 0
       would say the entity held no cash. Such an entity gets no bar and the card says why. */
    cash: (e.totals.unconverted || 0) >= e.totals.accounts ? null : e.totals.month_end_sgd,
  }));
  const cash = rows.filter((r) => r.cash != null).sort((a, b) => b.cash - a.cash);
  const noRate = rows.filter((r) => r.cash == null).map((r) => r.name);
  const h = Math.max(150, 46 + rows.length * 34);
  return (
    <div className="cw-charts" id="cw-charts">
      <Card title={t('cashc.monthCash')} sub={t('cashc.monthCashSub', { n: rows.length, of: data.entities.length })}>
        {noRate.length > 0 && cash.length > 0 && <p className="cw-chart-sub cw-down">{t('cashc.noRateSome', { list: noRate.join(', '), week: data.rule?.week || '' })}</p>}
        {!rows.length ? <Empty text={t('cashc.noneYet')} /> : !cash.length ? <Empty text={t('cashc.noRateAll', { week: data.rule?.week || '' })} /> : (
          <ResponsiveContainer width="100%" height={Math.max(150, 46 + cash.length * 34)}>
            <BarChart data={cash} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={compact} tick={axis} />
              <YAxis type="category" dataKey="name" width={56} tick={{ ...axis, fontWeight: 700 }} />
              <Tooltip formatter={(v, n, p) => [`${tip(v)}${p?.payload?.unconverted ? ` · ${t('cashm.unconverted', { n: p.payload.unconverted })}` : ''}`, n]} />
              <Bar dataKey="cash" name={t('cashm.h_monthEnd')} fill={C.monthEnd} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
      <Card title={t('cashc.recon')} sub={t('cashc.reconSub')}>
        {rows.length ? (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={axis} />
              <YAxis type="category" dataKey="name" width={56} tick={{ ...axis, fontWeight: 700 }} />
              <Tooltip formatter={(v, n) => [t('cashc.accounts', { count: v }), n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="reconciled" stackId="r" name={t('cashc.reconciled')} fill={C.reconciled} />
              <Bar dataKey="difference" stackId="r" name={t('cashc.difference')} fill={C.difference} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty text={t('cashc.noneYet')} />}
      </Card>
    </div>
  );
}
